import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link2, Unlink, CheckCircle2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { quickbooksAPI } from '@shared/apiClient';

/**
 * "Connect to QuickBooks" panel — OAuth connect/disconnect and connection
 * status. Actual invoice pushing happens per-call in the Invoicing mailbox
 * (customers only); this panel only manages the company-wide connection.
 */
export default function QuickbooksSettingsPanel() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery('quickbooksStatus', () => quickbooksAPI.getStatus());
  const status = data?.data;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qbo = params.get('qbo');
    if (!qbo) return;

    if (qbo === 'connected') {
      toast.success('QuickBooks connected');
      queryClient.invalidateQueries('quickbooksStatus');
    } else if (qbo === 'error') {
      toast.error(params.get('message') || 'Failed to connect QuickBooks');
    }

    params.delete('qbo');
    params.delete('message');
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    window.history.replaceState({}, '', newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectMutation = useMutation(() => quickbooksAPI.connect(), {
    onSuccess: (res) => {
      window.location.href = res.data.authUrl;
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to start QuickBooks connection');
    }
  });

  const disconnectMutation = useMutation(() => quickbooksAPI.disconnect(), {
    onSuccess: () => {
      queryClient.invalidateQueries('quickbooksStatus');
      toast.success('QuickBooks disconnected');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to disconnect QuickBooks');
    }
  });

  if (isLoading || !status) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-600">
        Loading QuickBooks status...
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-2">
          <Link2 className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">QuickBooks Online</h2>
        </div>
        <p className="text-sm text-gray-600">
          QuickBooks integration is not configured on this server. Ask your developer to set the QBO_* environment variables.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center mb-2">
        <Link2 className="h-5 w-5 text-gray-600 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">QuickBooks Online</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        When connected, completed customer (residential/commercial) service calls can be sent to QuickBooks as invoices from the Invoicing mailbox. C Spire and Franklin work are not affected.
      </p>

      {status.status === 'needs_reauth' && (
        <div className="mb-4 flex items-start rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            QuickBooks needs to be reconnected — the connection expired or was revoked.
            {status.lastError ? ` (${status.lastError})` : ''}
          </div>
        </div>
      )}

      {status.connected ? (
        <div className="space-y-3">
          <div className="flex items-center text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            Connected to <span className="font-medium ml-1">{status.companyName || 'QuickBooks'}</span>
            <span className="ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 capitalize">
              {status.environment}
            </span>
          </div>
          {status.itemMap?.labor?.id && status.itemMap?.parts?.id && (
            <p className="text-xs text-gray-500">
              Using QuickBooks items "{status.itemMap.labor.name}" and "{status.itemMap.parts.name}" for invoice lines.
            </p>
          )}
          <button
            type="button"
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50"
          >
            <Unlink className="h-4 w-4 mr-1.5" />
            Disconnect QuickBooks
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => connectMutation.mutate()}
          disabled={connectMutation.isLoading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          <Link2 className="h-4 w-4 mr-1.5" />
          {status.status === 'needs_reauth' ? 'Reconnect QuickBooks' : 'Connect to QuickBooks'}
        </button>
      )}
    </div>
  );
}
